/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-26
 */

// Add third-party dependencies.
import { Button, Badge, Box, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactNode } from 'react';

/**
 * Props for the NavigationItem component.
 */
interface NavigationItemProps {
  /**
   * The display label for the navigation item.
   */
  label: string;
  /**
   * The path to navigate to when clicked.
   */
  path: string;
  /**
   * Optional icon to display before the label.
   */
  icon?: ReactNode;
  /**
   * Optional badge content (number or text).
   */
  badge?: string | number;
  /**
   * Whether the item is currently active.
   * If not provided, will be determined by current path.
   */
  isActive?: boolean;
  /**
   * Additional CSS classes.
   */
  className?: string;
  /**
   * Click handler override. If provided, navigation will not occur automatically.
   */
  onClick?: () => void;
  /**
   * Whether the item is disabled.
   * @default false
   */
  disabled?: boolean;
}

/**
 * Reusable navigation item component with consistent styling and behavior.
 * Supports icons, badges, active states, and accessibility features.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The navigation item component
 *
 * @example
 * return (
 *   <NavigationItem
 *     label="Dashboard"
 *     path="/dashboard"
 *     icon={<DashboardIcon />}
 *     requiresAuth={true}
 *   />
 * );
 *
 * @example
 * // With badge
 * return (
 *   <NavigationItem
 *     label="Messages"
 *     path="/messages"
 *     badge={5}
 *   />
 * );
 */
const NavigationItem: React.FC<NavigationItemProps> = ({
  label,
  path,
  icon,
  badge,
  isActive,
  className,
  onClick,
  disabled = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if this item is active
  const active = isActive !== undefined ? isActive : location.pathname === path;

  const handleClick = () => {
    if (disabled) return;

    if (onClick) {
      onClick();
    } else {
      navigate(path);
    }
  };

  const buttonContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon && <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>}
      <Typography
        variant="body2"
        sx={{
          fontWeight: active ? 600 : 500,
          color: 'inherit',
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  return (
    <Button
      onClick={handleClick}
      disabled={disabled}
      className={className}
      sx={{
        color: active ? '#1976d2' : 'inherit',
        backgroundColor: active ? '#ffffff' : 'transparent',
        fontWeight: active ? 600 : 500,
        textTransform: 'none',
        borderRadius: 1,
        px: 2,
        py: 1,
        minWidth: 'auto',
        border: active
          ? '1px solid rgba(25, 118, 210, 0.2)'
          : '1px solid transparent',
        '&:hover': {
          backgroundColor: active ? '#f5f5f5' : 'rgba(255, 255, 255, 0.08)',
          color: active ? '#1565c0' : 'inherit',
        },
        '&:focus': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '2px',
        },
      }}
      aria-label={`Navigate to ${label}`}
      aria-current={active ? 'page' : undefined}
    >
      {badge ? (
        <Badge
          badgeContent={badge}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.75rem',
              height: 18,
              minWidth: 18,
            },
          }}
        >
          {buttonContent}
        </Badge>
      ) : (
        buttonContent
      )}
    </Button>
  );
};

export default NavigationItem;
